<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-6">
      <h1>Integrations</h1>

      <div class="w-full max-w-3xl flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <i class="pi pi-google text-xl text-surface-400" aria-hidden="true"></i>
          <h2 class="m-0 text-lg">Google Drive</h2>
        </div>
        <p class="m-0 text-sm text-surface-400">
          Export product photos straight into a folder on Google Drive. Phottr only ever sees
          the folder you choose here — nothing else in your Drive.
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            label="Connect existing folder"
            icon="pi pi-folder-open"
            size="small"
            :loading="connecting === 'pick'"
            :disabled="connecting !== null"
            @click="connect('pick')"
          />
          <Button
            label="Create Phottr folder"
            icon="pi pi-plus"
            size="small"
            outlined
            :loading="connecting === 'create'"
            :disabled="connecting !== null"
            @click="connect('create')"
          />
        </div>
      </div>

      <DataTable :value="integrations" :loading="loading" tableStyle="min-width: 40rem">
        <Column field="name" header="Integration" />
        <Column header="Type">
          <template #body="{ data }">
            {{ typeLabel(data.type) }}
          </template>
        </Column>
        <Column header="Google account">
          <template #body="{ data }">
            {{ driveSettings[data.id]?.googleAccountEmail ?? '—' }}
          </template>
        </Column>
        <Column header="Folder">
          <template #body="{ data }">
            <!-- The folder name alone does not say where in Drive it lives, so link to it. -->
            <a
              v-if="driveSettings[data.id]"
              :href="driveSettings[data.id].folderUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1"
            >
              {{ driveSettings[data.id].folderName }}
              <i class="pi pi-external-link text-xs" aria-hidden="true"></i>
            </a>
            <span v-else>—</span>
          </template>
        </Column>
        <Column header="Connected">
          <template #body="{ data }">
            {{ driveSettings[data.id] ? formatDate(driveSettings[data.id].created) : '—' }}
          </template>
        </Column>
        <Column header="Status">
          <template #body="{ data }">
            <Tag
              v-if="driveSettings[data.id]"
              :severity="driveSettings[data.id].status === 'active' ? 'success' : 'warn'"
              :value="driveSettings[data.id].status === 'active' ? 'Active' : 'Needs reconnect'"
            />
            <span v-else>—</span>
          </template>
        </Column>
        <Column>
          <template #body="{ data }">
            <Button
              v-if="data.type === 'google-drive'"
              label="Disconnect"
              severity="danger"
              size="small"
              outlined
              @click="confirmDisconnect(data.id)"
            />
          </template>
        </Column>
        <template #empty>No integrations yet.</template>
      </DataTable>
    </div>

    <Dialog
      v-model:visible="showDisconnect"
      modal
      header="Disconnect Google Drive?"
      :style="{ width: '28rem' }"
    >
      <p class="m-0 text-sm">
        Phottr will lose access to the folder. Photos already exported stay in your Google
        Drive.
      </p>
      <template #footer>
        <Button label="Cancel" text @click="showDisconnect = false" />
        <Button label="Disconnect" severity="danger" :loading="disconnecting" @click="disconnect" />
      </template>
    </Dialog>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { useToast } from 'primevue/usetoast';
import type { GoogleDriveIntegrationSettingsDto, IntegrationDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Tag from 'primevue/tag';

const { api } = useApiClient();
const toast = useToast();
const route = useRoute();
const router = useRouter();

const integrations = ref<IntegrationDto[]>([]);
const driveSettings = ref<Record<string, GoogleDriveIntegrationSettingsDto>>({});
const loading = ref(false);
const connecting = ref<'pick' | 'create' | null>(null);
const disconnectId = ref<string | null>(null);
const showDisconnect = ref(false);
const disconnecting = ref(false);

const typeLabels: Record<string, string> = {
  'google-drive': 'Google Drive',
  shoptet: 'Shoptet',
  make: 'Make',
};

function typeLabel(type: string): string {
  return typeLabels[type] ?? type;
}

function formatDate(epochMs: number): string {
  return dayjs(epochMs).format('MMM D, YYYY HH:mm');
}

/** Human-readable text for the short error codes on the callback redirect. */
const connectErrors: Record<string, string> = {
  'access-denied': 'Google Drive access was not granted. Try connecting again.',
  'invalid-state': 'The connection took too long. Please start again.',
  'no-refresh-token':
    'Google did not grant Phottr lasting access. Remove Phottr from your Google account’s third-party apps and try again.',
  'no-folder-picked': 'No folder was selected. Choose a folder and try again.',
};

async function loadIntegrations() {
  loading.value = true;
  try {
    const { data, error } = await api.appControllerIntegrations();
    if (error) {
      throw new Error('Failed to load integrations');
    }
    integrations.value = data ?? [];

    const settings: Record<string, GoogleDriveIntegrationSettingsDto> = {};
    await Promise.all(
      integrations.value
        .filter(integration => integration.type === 'google-drive')
        .map(async integration => {
          const res = await api.googleDriveControllerGetSettings({
            path: { integrationId: integration.id },
          });
          if (res.data) {
            settings[integration.id] = res.data;
          }
        }),
    );
    driveSettings.value = settings;
  } finally {
    loading.value = false;
  }
}

async function connect(folderMode: 'pick' | 'create') {
  connecting.value = folderMode;
  try {
    const { data, error } = await api.googleDriveControllerStartConnect({
      body: { platform: 'web', folderMode },
    });
    if (error || !data) {
      throw new Error('Failed to start connecting Google Drive');
    }
    // Google's consent screen must run in a top-level browser window, not an iframe.
    window.location.assign(data.authorizeUrl);
  } catch (e) {
    connecting.value = null;
    toast.add({
      severity: 'error',
      summary: 'Could not start connecting Google Drive',
      life: 5000,
    });
    throw e;
  }
}

function confirmDisconnect(integrationId: string) {
  disconnectId.value = integrationId;
  showDisconnect.value = true;
}

async function disconnect() {
  const integrationId = disconnectId.value;
  if (!integrationId) return;

  disconnecting.value = true;
  try {
    const { error } = await api.googleDriveControllerDeleteIntegration({
      path: { integrationId },
    });
    if (error) {
      throw new Error('Failed to disconnect Google Drive');
    }
    toast.add({ severity: 'success', summary: 'Google Drive disconnected', life: 3000 });
    showDisconnect.value = false;
    disconnectId.value = null;
    await loadIntegrations();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Failed to disconnect Google Drive', life: 5000 });
    throw e;
  } finally {
    disconnecting.value = false;
  }
}

/** Reads the ?googleDrive= result the backend callback redirected us back with. */
function handleCallbackResult() {
  const status = route.query.googleDrive;
  if (typeof status !== 'string') return;

  if (status === 'connected') {
    toast.add({ severity: 'success', summary: 'Google Drive connected', life: 3000 });
  } else {
    const code = typeof route.query.code === 'string' ? route.query.code : '';
    toast.add({
      severity: 'error',
      summary: 'Connecting Google Drive failed',
      detail: connectErrors[code] ?? 'Please try again.',
      life: 8000,
    });
  }

  router.replace({ path: route.path });
}

handleCallbackResult();
loadIntegrations();
</script>
